<template>
  <div>
    <div class="bg-container"></div>
    <img class="labour-love" src="@/assets/images/labour-love.svg">

    <bear-doc class="bear-doc-svg"></bear-doc>

    <div class="doctors-copy-container">
      <div class="heading">
        <div class="heading-line-1">Celebrating our</div>
        <strong class="heading-line-2">DOCTORS</strong>
      </div>
      <div class="clipboard-container">
        <img class="clipboard" src="@/assets/images/doctors/clipboard.svg">
        <div class="doctors-names">
          <div
            v-for="(doctor, index) in $store.getters.activeSlide.doctors"
            :key="`doctor-item-${index}`"
            class="doctor-name"
            :class="`doctor-name-${index}`">

            <name-container
              is-one-line
              use-split-text
              :text="`${doctor.firstname} ${doctor.lastname}`"
              :chars-limit-one="18"
              :chars-limit-two="21"
              :chars-limit-three="24"
              size-three="0.6em" ></name-container>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TimelineMax, Power0, Power3, Power4 } from 'gsap'
import SplitText from '@/assets/gsap-bonus/SplitText'
import BearDoc from '@/components/DoctorsSlide/BearDoc.vue'
import NameContainer from '@/components/NameContainer.vue'

export default {
  name: 'DoctorsSlide',

  components: { BearDoc, NameContainer },

  data () {
    return {
      tl: new TimelineMax(),
      tlEyes: new TimelineMax({ repeat: -1, repeatDelay: 3 }),
      tlName: new TimelineMax(),
      namesUpdated: false
    }
  },

  mounted () {
    // Copy elements
    const heading1 = this.$el.querySelector('.heading-line-1')
    const heading2 = this.$el.querySelector('.heading-line-2')
    const clipboardContainer = this.$el.querySelector('.clipboard-container')
    const headingSplit = new SplitText(heading1, { type: 'chars' })

    // SVG elements
    const bearDoc = this.$el.querySelector('.bear-doc-svg')
    const bear = this.$el.querySelector('.docslide-bear')
    const cubEar1 = this.$el.querySelector('.cub-ear-1')
    const cubEar2 = this.$el.querySelector('.cub-ear-2')
    const bearEar = this.$el.querySelector('.bear-ear')
    const bearEyelid = this.$el.querySelector('.bear-eyelid')
    const bearGlare = this.$el.querySelector('.bear-glare')
    const doc = this.$el.querySelector('.docslide-doc')
    const docArm1 = this.$el.querySelector('.doc-arm-1')
    const docArm2 = this.$el.querySelector('.doc-arm-2')
    const docBun = this.$el.querySelector('.doc-bun')
    const docEyelids = this.$el.querySelectorAll('.doc-eyelid')
    const smile = this.$el.querySelector('.smile')
    const smileBig = this.$el.querySelector('.smile-big')

    this.tlEyes
      .delay(1.5)
      .to(bearEyelid, 0.3, { yPercent: -85, ease: Power0.easeNone, repeat: 1, yoyo: true, repeatDelay: 0.3 }, 0)
      .to(bearGlare, 0.3, { opacity: 0, repeat: 1, yoyo: true, repeatDelay: 0.3 }, 0)
      .to(docEyelids, 0.3, { yPercent: -85, ease: Power0.easeNone, repeat: 1, yoyo: true, repeatDelay: 0.3 }, 1)

    this.tl
      .delay(1.2)
      .add('start')
      .staggerFrom(headingSplit.chars, 0.8, { yPercent: 60, opacity: 0, ease: Power3.easeOut }, 0.04, 'start')
      .from(bearDoc, 1.7, { xPercent: -150, ease: Power4.easeOut }, 'start')
      .from(heading1, 1.7, { yPercent: 150, ease: Power4.easeOut }, 'start+=0.1')
      .from(heading2, 1.7, { yPercent: 150, ease: Power4.easeOut }, 'start+=0.2')
      .from(clipboardContainer, 1.7, { yPercent: 150, ease: Power4.easeOut }, 'start')
      .add('loop', 0)
      .to(bear, 1.7, { rotation: 1, transformOrigin: '50% 100%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(cubEar1, 1.7, { rotation: 10, transformOrigin: '80% 80%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(cubEar2, 1.7, { rotation: -10, transformOrigin: '0% 100%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(bearEar, 1.7, { rotation: -15, transformOrigin: '50% 50%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(doc, 1.7, { rotation: -1, transformOrigin: '50% 100%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(docArm1, 1.7, { rotation: -5, transformOrigin: '100% 10%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(docArm2, 1.7, { rotation: 3, transformOrigin: '90% 0%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(docBun, 1.7, { rotation: 5, transformOrigin: '10% 80%', ease: Power0.easeNone, repeat: -1, yoyo: true }, 'loop')
      .to(smile, 1.7, { scale: 0.7, rotation: -5, transformOrigin: 'center center', ease: Power0.easeInOut, yoyo: true, repeat: -1 }, 'loop')
      .to(smileBig, 1.7, { scaleY: 0.8, rotation: -15, xPercent: -5, yPercent: 10, transformOrigin: 'center center', ease: Power0.easeInOut, yoyo: true, repeat: -1 }, 'loop')

    this.namesIn(1.5)
  },

  watch: {
    '$store.state.activeSlide' () {
      this.namesUpdated = true
    }
  },

  updated () {
    if (this.namesUpdated) {
      this.namesUpdated = false
      this.namesIn()
    }
  },

  beforeDestroy () {
    this.tl.kill()
    this.tlEyes.kill()
    this.tlName.kill()
  },

  methods: {
    namesIn (delay = 0) {
      const nameChars1 = this.$el.querySelectorAll('.doctor-name-0 .split-char')
      const nameChars2 = this.$el.querySelectorAll('.doctor-name-1 .split-char')
      const nameChars3 = this.$el.querySelectorAll('.doctor-name-2 .split-char')
      const nameChars4 = this.$el.querySelectorAll('.doctor-name-3 .split-char')

      this.tlName.kill()
      this.tlName = new TimelineMax()
        .delay(delay)
        .staggerFrom(nameChars1, 0.1, { opacity: 0 }, 0.03, 0)
        .staggerFrom(nameChars2, 0.1, { opacity: 0 }, 0.03, 0.25)
        .staggerFrom(nameChars3, 0.1, { opacity: 0 }, 0.03, 0.5)
        .staggerFrom(nameChars4, 0.1, { opacity: 0 }, 0.03, 0.75)
    }
  }
}
</script>

<style scoped>
.labour-love {
  position: absolute;
  height: auto;
  width: 5.7vw;
  left: 2vw;
  bottom: 2vw;
  z-index: 5;
}

.bg-container {
  background: #fffdf2;
  width: 100vw;
  height: 100vh;
}

.bear-doc-svg {
  position: absolute;
  width: 41.2vw;
  left: 14.9vw;
  bottom: 0;
}

.doctors-copy-container {
  position: absolute;
  right: 10.6vw;
  width: 28.1vw;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.heading {
  font-family: 'Barlow', sans-serif;
  font-weight: 300;
  font-size: 3.6vw;
  color: #59bec9;
  text-align: center;
  width: 100%;
  line-height: 1.05;
  overflow: hidden;
}

.heading-line-2 {
  font-weight: 500;
  font-size: 1.6em;
  overflow: hidden;
  display: block;
}

.clipboard-container {
  position: relative;
  width: 100%;
  margin-top: 2vw;
}

.clipboard {
  width: 100%;
  height: auto;
}

.doctors-names {
  position: absolute;
  top: 39%;
  left: 15%;
  right: 15%;
  text-align: center;
}

.doctor-name {
  font-family: 'Barlow', sans-serif;
  font-weight: 700;
  font-size: 1.9vw;
  height: 1.9vw;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.doctor-name:not(:first-child) {
  margin-top: 6.5%;
}

</style>



// WEBPACK FOOTER //
// src/components/DoctorsSlide.vue